import {Avatar, Button, Dialog, DialogActions, DialogBody, DialogTitle, InputField, Textarea} from '@nlabs/gothamjs/components';
import {BookOpen, Plus, Users} from '@nlabs/gothamjs/icons';
import {useState} from 'react';

import {
  fieldLabel, glassPanel, mutedText, pageHeading, primaryButton,
  secondaryButton, textArea, textInput
} from '../../theme/classes.js';
import {CharacterContinuity} from '../CharacterContinuity/CharacterContinuity.js';

import type {Project} from '@reactorbox/shared';
import type {FormEvent, ReactElement} from 'react';
import type {WorkspaceActions} from '../../state/workspaceActions.js';

interface CharactersProps {readonly actions: WorkspaceActions;
  readonly busy: boolean;
  readonly project: Project;}
export const Characters = ({actions, busy, project}: CharactersProps): ReactElement => {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if(!name.trim()) {
      return;
    }
    void actions.mutate(`/api/projects/${project.id}/characters`, {description: description.trim(), name: name.trim(), referenceUrl: referenceUrl.trim() || undefined}, 'Character added to your story bible.');
  };
  return <div className="mx-auto w-full max-w-7xl space-y-8 px-4 py-6 sm:px-7 sm:py-9 lg:px-10">
    <div className="flex items-center gap-3 text-xs text-white/40">
      <span className="truncate">{project.name}</span><span>/</span><span className="text-white/70">Characters</span>
    </div>
    <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
      <div className="max-w-2xl space-y-3">
        <h1 className={pageHeading}>A familiar face. Every frame<span className="text-violet-300">.</span></h1>
        <p className={mutedText}>Keep your characters and their details together, across the whole story.</p>
      </div>
      <Button className={`${primaryButton} shrink-0!`} icon={<Plus size={16} />} onClick={() => setAdding(true)}>
        Add character
      </Button>
    </div>
    <div className={`${glassPanel} flex items-start gap-4 p-5 sm:items-center sm:p-6`}>
      <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-violet-300/10 text-violet-200"><BookOpen size={20} /></span>
      <div className="space-y-1">
        <strong className="text-sm font-medium text-white/85">{project.name} · Story bible</strong>
        <p className={mutedText}>
          Character descriptions are included in shot prompts. Reference support depends on the generation model.
        </p>
      </div>
    </div>
    <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
      {project.characters.map((character) => <article className={`${glassPanel} flex min-w-0 flex-col p-6 transition hover:border-white/20 hover:bg-white/[0.07]`} key={character.id}>
        <div className="mb-7 flex items-start justify-between gap-3">
          <Avatar className="size-18! border! border-white/15! bg-violet-200/15! text-xl! text-violet-100!" initials={character.name.split(' ').map((part) => part[0]).slice(0, 2).join('')} size="lg" src={character.referenceUrl} />
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/60">{project.shots.filter((shot) => shot.characterIds.includes(character.id)).length} shots</span>
        </div>
        <h2 className="mb-3 text-2xl font-normal tracking-tight text-violet-200">{character.name}</h2>
        <p className={`${mutedText} flex-1 break-words`}>{character.description || 'Add this character to a shot to carry their identity into the prompt.'}</p>
        <CharacterContinuity
          actions={actions} busy={busy} character={character}
          key={JSON.stringify(character.continuity)} project={project}
        />
        <div className="mt-7 flex items-center gap-2 border-t border-white/10 pt-4 text-xs text-white/35"><Users size={14} />Project character</div>
      </article>)}
      <button className="group flex min-h-72 flex-col items-center justify-center rounded-[2rem] border border-dashed border-white/15 p-7 text-center transition hover:border-white/30 hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-violet-300" onClick={() => setAdding(true)} type="button">
        <span className="mb-5 flex size-14 items-center justify-center rounded-full bg-white/5 text-white/60 transition group-hover:bg-white/10 group-hover:text-white"><Plus size={22} /></span>
        <strong className="text-base font-medium text-white/80">Someone new to the story</strong>
        <p className={`${mutedText} mt-2`}>Define their look, personality, and role.</p>
      </button>
    </div>
    <Dialog aria-label="Add character" className="rounded-[2rem]! border-white/15! bg-zinc-950/95! p-6! text-white! shadow-2xl! shadow-black/50! ring-white/5! sm:p-8!" onClose={setAdding} open={adding} size="lg">
      <DialogTitle className="text-2xl! font-normal! text-white!">Add a character</DialogTitle>
      <form onSubmit={submit}>
        <DialogBody className="space-y-5! text-white!">
          <div>
            <label className={fieldLabel} htmlFor="character-name">Character name</label>
            <InputField aria-label="Character name" autoFocus borderType="rounded" className={textInput} id="character-name" label="Character name" maxLength={100} onChange={(event) => setName(event.target.value)} required value={name} />
          </div>
          <div>
            <label className={fieldLabel} htmlFor="character-description">Appearance and identity</label>
            <Textarea className={textArea} id="character-description" maxLength={3000} onChange={(event) => setDescription(event.target.value)} placeholder="Who are they? Describe details that should stay consistent across shots." rows={5} value={description} />
          </div>
          <div>
            <label className={fieldLabel} htmlFor="character-reference">Reference image URL <span className="ml-1 font-normal text-white/35">optional</span></label>
            <InputField borderType="rounded" className={textInput} id="character-reference" onChange={(event) => setReferenceUrl(event.target.value)} placeholder="https://…" type="url" value={referenceUrl} />
          </div>
        </DialogBody>
        <DialogActions className="border-t border-white/10 pt-5">
          <Button className={secondaryButton} onClick={() => setAdding(false)} variant="outline">Close</Button>
          <Button className={primaryButton} disabled={busy || !name.trim()} type="submit">Save character</Button>
        </DialogActions>
      </form>
    </Dialog>
  </div>;
};
